/**
 * Centralized dataset configuration for SQL exercises
 * All exercises use the same datasets from Supabase storage
 */

export const EXERCISE_DATASETS = {
  // Main bucket for e-commerce data
  SUPABASE_BUCKET: "ecommerce_data",

  // Product analytics bucket
  PRODUCT_BUCKET: "product_data",

  // SQL Introduction/Primer bucket
  INTRO_BUCKET: "sql_intro_dataset",

  // SQL Intermediate bucket (Streaming TV)
  INT_BUCKET: "sql_intermediate_dataset",

  // Fintech dataset bucket
  FINTECH_BUCKET: "fintech_dataset",

  // Available dataset files
  FILES: {
    ACCOUNTS: "accounts.csv",
    ORDERS: "orders.csv",
    REGION: "region.csv",
    SALES_REPS: "sales_reps.csv",
    WEB_EVENTS: "web_events.csv",
    // Product analytics files
    USERS: "users.csv",
    SESSIONS: "sessions.csv",
    EVENTS: "events.csv",
    // SQL Intro files
    INTRO_USERS: "users.csv",
    INTRO_ORDERS: "orders.csv",
    INTRO_ORDER_ITEMS: "order_items.csv",
    INTRO_PAYMENTS: "payments.csv",

    // SQL Intermediate files
    INT_CONTENT: "content.csv",
    INT_SUBSCRIPTIONS: "subscriptions.csv",
    INT_SESSIONS: "viewing_sessions.csv",

    // Fintech files
    FINTECH_TRANSACTIONS: "transactions.csv",
    FINTECH_REFUNDS: "refunds.csv",
    FINTECH_CHARGEBACKS: "chargebacks.csv",
    FINTECH_FX_RATES: "fx_rates.csv"
  },

  // Common dataset combinations for different exercise types
  COMBINATIONS: {
    // Basic exercises - just accounts and orders
    BASIC: ["accounts.csv", "orders.csv"],

    // Full dataset - all tables
    FULL: [
      "accounts.csv",
      "orders.csv",
      "region.csv",
      "sales_reps.csv",
      "web_events.csv"
    ],

    // Sales analysis - accounts, orders, reps, region
    SALES: [
      "accounts.csv",
      "orders.csv",
      "region.csv",
      "sales_reps.csv"
    ],

    // Web analytics - accounts, orders, web events
    WEB: [
      "accounts.csv",
      "orders.csv",
      "web_events.csv"
    ],

    // Product analytics - users, sessions, events
    PRODUCT: [
      "users.csv",
      "sessions.csv",
      "events.csv"
    ],

    // SQL Introduction - users, orders, order_items, payments
    INTRO: [
      { name: "users.csv", bucket: "sql_intro_dataset" },
      { name: "orders.csv", bucket: "sql_intro_dataset" },
      { name: "order_items.csv", bucket: "sql_intro_dataset" },
      { name: "payments.csv", bucket: "sql_intro_dataset" }
    ],

    // SQL Intermediate - content, subscriptions, sessions, users
    INTERMEDIATE: [
      { name: "users_v2.csv", bucket: "sql_intermediate_dataset", tableAlias: 'users' },
      { name: "content.csv", bucket: "sql_intermediate_dataset" },
      { name: "subscriptions.csv", bucket: "sql_intermediate_dataset" },
      { name: "viewing_sessions.csv", bucket: "sql_intermediate_dataset" }
    ],

    // Fintech - transactions, refunds, chargebacks, fx_rates
    FINTECH: [
      { name: "transactions.csv", bucket: "fintech_dataset" },
      { name: "refunds.csv", bucket: "fintech_dataset" },
      { name: "chargebacks.csv", bucket: "fintech_dataset" },
      { name: "fx_rates.csv", bucket: "fintech_dataset" }
    ]
  }
};

/**
 * Get dataset configuration for a specific exercise type
 * Returns stable references to prevent unnecessary re-renders
 * @param {string} type - Type of exercise (BASIC, FULL, SALES, WEB, PRODUCT, INTRO, B2B)
 * @param {string} bucket - Optional bucket override
 * @returns {object} Configuration object with bucket and files
 */

// Simple configuration retrieval - avoiding caching to prevent React reference issues
export const getExerciseDatasets = (type = 'BASIC', bucket = null) => {
  const datasetFiles = EXERCISE_DATASETS.COMBINATIONS[type] || EXERCISE_DATASETS.COMBINATIONS.BASIC;

  // Auto-select bucket based on type if not specified
  let supabaseBucket = bucket;
  if (!supabaseBucket) {
    if (type === 'PRODUCT') {
      supabaseBucket = EXERCISE_DATASETS.PRODUCT_BUCKET;
    } else if (type === 'INTRO') {
      supabaseBucket = EXERCISE_DATASETS.INTRO_BUCKET;
    } else if (type === 'INTERMEDIATE') {
      supabaseBucket = EXERCISE_DATASETS.INT_BUCKET;
    } else if (type === 'FINTECH') {
      supabaseBucket = EXERCISE_DATASETS.FINTECH_BUCKET;
    } else if (type === 'DATA_VIZ_1') {
      supabaseBucket = EXERCISE_DATASETS.DATA_VIZ_1_BUCKET;
    } else if (type === 'DATA_VIZ_2') {
      supabaseBucket = EXERCISE_DATASETS.DATA_VIZ_2_BUCKET;
    } else if (type === 'DATA_VIZ_3') {
      supabaseBucket = EXERCISE_DATASETS.DATA_VIZ_3_BUCKET;
    } else if (type === 'DATA_VIZ_INTRO') {
      supabaseBucket = EXERCISE_DATASETS.DATA_VIZ_INTRO_BUCKET;
    } else if (type === 'DATA_VIZ_INTERMEDIATE') {
      supabaseBucket = EXERCISE_DATASETS.DATA_VIZ_INTERMEDIATE_BUCKET;
    } else {
      supabaseBucket = EXERCISE_DATASETS.SUPABASE_BUCKET;
    }
  }

  return {
    supabaseBucket,
    datasetFiles
  };
};

// Add B2B configuration (used by Case 1 and 2)
EXERCISE_DATASETS.COMBINATIONS.B2B = ["regions.csv", "sales_reps.csv", "accounts.csv", "orders.csv"];

// Data Visualization Module 1 - App Activity Dataset
EXERCISE_DATASETS.DATA_VIZ_1_BUCKET = "data_viz_1";
EXERCISE_DATASETS.COMBINATIONS.DATA_VIZ_1 = [
  { name: "users_v3.csv", bucket: "data_viz_1", tableAlias: "users" },
  { name: "events_v3.csv", bucket: "data_viz_1", tableAlias: "events" }
];

// Data Visualization Module 2 - Customer Financial Behavior Dataset
EXERCISE_DATASETS.DATA_VIZ_2_BUCKET = "data_viz_2";
EXERCISE_DATASETS.COMBINATIONS.DATA_VIZ_2 = [
  { name: "customers_v2.csv", bucket: "data_viz_2", tableAlias: "customers" },
  { name: "transactions_v2.csv", bucket: "data_viz_2", tableAlias: "transactions" }
];

// Data Visualization Module 3 - Ride-Sharing Platform Operations Dataset
EXERCISE_DATASETS.DATA_VIZ_3_BUCKET = "data_viz_3";
EXERCISE_DATASETS.COMBINATIONS.DATA_VIZ_3 = [
  { name: "drivers.csv", bucket: "data_viz_3", tableAlias: "drivers" },
  { name: "trips.csv", bucket: "data_viz_3", tableAlias: "trips" }
];

// Data Visualization Intro Module - Video Streaming Dataset
EXERCISE_DATASETS.DATA_VIZ_INTRO_BUCKET = "data_viz_intro";
EXERCISE_DATASETS.COMBINATIONS.DATA_VIZ_INTRO = [
  { name: "users_2.csv", bucket: "data_viz_intro", tableAlias: "users" },
  { name: "watch_events_2.csv", bucket: "data_viz_intro", tableAlias: "watch_events" }
];

// Data Visualization Intermediate Module - E-commerce Dataset
EXERCISE_DATASETS.DATA_VIZ_INTERMEDIATE_BUCKET = "data_viz_intermediate";
EXERCISE_DATASETS.COMBINATIONS.DATA_VIZ_INTERMEDIATE = [
  { name: "customers.csv", bucket: "data_viz_intermediate", tableAlias: "customers" },
  { name: "orders.csv", bucket: "data_viz_intermediate", tableAlias: "orders" }
];
